/* H23: hero video loading guard.
 *
 * Two jobs, both about not shipping ~6.5 MB of video to people who cannot or
 * should not receive it.
 *
 * 1. BREAKPOINT UNLOADING
 *    The hero renders both a desktop and a mobile video and hides one with
 *    CSS. `display: none` does NOT stop a browser fetching a video, so without
 *    this the homepage downloads BOTH files, about 13 MB. We strip the
 *    sources off whichever one is hidden at the current breakpoint and abort
 *    its request.
 *
 *    The theme's own breakpoint (assets/critical.css) is 1200px:
 *      .wt-mobile-hidden   -> hidden below 1200px  (the desktop video)
 *      .wt-desktop-hidden  -> hidden at/above 1200px (the mobile video)
 *
 * 2. REDUCED MOTION / DATA SAVER
 *    An autoplaying 6.5 MB loop with no opt-out is an accessibility problem,
 *    not just a performance one. Looping motion can trigger vestibular
 *    symptoms and migraine. Anyone whose OS asks for reduced motion, or who
 *    has data saver on, keeps the poster frame instead.
 *
 * A MutationObserver catches each <video> as the parser reaches it, so the
 * decision lands before the fetch gets far. Nothing here depends on
 * stylesheets having applied yet.
 */
(function () {
  'use strict';

  var DESKTOP_MIN_WIDTH = 1200;
  var STORE_KEY = 'h23HeroSources';

  function prefersNoMotion() {
    return (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function prefersLessData() {
    var conn =
      navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    return !!(conn && conn.saveData);
  }

  function isDesktop() {
    return window.innerWidth >= DESKTOP_MIN_WIDTH;
  }

  /* Is this video the one CSS is hiding right now? */
  function isHiddenAtCurrentBreakpoint(video) {
    var cls = video.className || '';
    if (cls.indexOf('wt-mobile-hidden') !== -1) return !isDesktop();
    if (cls.indexOf('wt-desktop-hidden') !== -1) return isDesktop();
    return false;
  }

  function detach(video) {
    if (video.dataset[STORE_KEY]) return; // already detached

    var sources = [];
    var nodes = video.querySelectorAll('source');

    for (var i = 0; i < nodes.length; i++) {
      sources.push({ src: nodes[i].src, type: nodes[i].type || '' });
    }
    if (!sources.length && video.src) {
      sources.push({ src: video.src, type: '' });
    }
    if (!sources.length) return;

    video.dataset[STORE_KEY] = JSON.stringify(sources);

    try {
      video.pause();
    } catch (e) {
      /* pausing a not-yet-playing video can throw; harmless */
    }

    while (video.firstChild) video.removeChild(video.firstChild);
    video.removeAttribute('src');
    video.setAttribute('preload', 'none');
    video.load(); // aborts any request already in flight
  }

  function attach(video) {
    var stored = video.dataset[STORE_KEY];
    if (!stored) return;

    var sources;
    try {
      sources = JSON.parse(stored);
    } catch (e) {
      return;
    }

    for (var i = 0; i < sources.length; i++) {
      var source = document.createElement('source');
      source.src = sources[i].src;
      if (sources[i].type) source.type = sources[i].type;
      video.appendChild(source);
    }

    delete video.dataset[STORE_KEY];
    video.setAttribute('preload', 'metadata');
    video.load();

    if (video.dataset.h23Autoplay === '1' && !prefersNoMotion() && !prefersLessData()) {
      var attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') attempt.catch(function () {});
    }
  }

  function process(video) {
    if (video.hasAttribute('autoplay')) video.dataset.h23Autoplay = '1';

    if (prefersNoMotion() || prefersLessData()) {
      video.autoplay = false;
      video.removeAttribute('autoplay');
      video.setAttribute('preload', 'none');
      try {
        video.pause();
      } catch (e) {
        /* see above */
      }
      return;
    }

    if (isHiddenAtCurrentBreakpoint(video)) {
      detach(video);
    } else {
      attach(video);
    }
  }

  function processAll() {
    var videos = document.querySelectorAll('video');
    for (var i = 0; i < videos.length; i++) process(videos[i]);
  }

  /* Catch each <video> the moment the parser produces it. */
  if (typeof MutationObserver === 'function') {
    var observer = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.tagName === 'VIDEO') {
            process(node);
          } else if (node.querySelectorAll) {
            var nested = node.querySelectorAll('video');
            for (var k = 0; k < nested.length; k++) process(nested[k]);
          }
        }
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', processAll);
  } else {
    processAll();
  }

  /* Re-evaluate when the viewport crosses the breakpoint. */
  var resizeTimer = null;
  window.addEventListener('resize', function () {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(processAll, 200);
  });

  document.addEventListener('shopify:section:load', processAll);
})();
